import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { CalendarEvent } from "@/components/calendar/views/CalendarEventTypes";
import { eventColor } from "@/lib/calendar/mobile-agenda";
import { getStatusSoftColorStyle } from "@/components/calendar/views/calendarEventUtils";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";

type Props = {
  event: CalendarEvent;
  statuses: AppointmentStatus[];
  statusLabel: string;
  onClick: (id: string) => void;
};

export function MobileEventCard({ event, statuses, statusLabel, onClick }: Props) {
  const { t } = useTranslation("calendar");
  const time = format(new Date(event.start), "HH:mm", { locale: ptBR });
  const vetName = event.vet?.full_name ?? "";
  const isPaid = event.financial_status === "pago";

  return (
    <button
      type="button"
      onClick={() => onClick(event.id)}
      className="flex w-full gap-3 rounded-2xl border border-border/70 bg-card p-3.5 text-left shadow-sm transition-colors active:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span
        aria-hidden
        className="w-1 shrink-0 rounded-full"
        style={{ backgroundColor: eventColor(event) }}
      />
      <span className="min-w-0 flex-1">
        <span className="mb-1 flex items-center justify-between gap-2">
          <span className="truncate text-[15px] font-bold text-foreground">{event.title}</span>
          <span className="shrink-0 text-[13px] font-semibold tabular-nums text-muted-foreground">{time}</span>
        </span>
        <span className="mb-2 block truncate text-[13px] text-foreground/80">
          {event.clinic_appointment_type?.name ?? t("appointmentLabel")}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          <span
            className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
            style={getStatusSoftColorStyle(event.status, statuses)}
          >
            {statusLabel}
          </span>
          {isPaid ? (
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              {t("mobile.paid")}
            </span>
          ) : null}
          {vetName ? <span className="text-[12px] text-muted-foreground">{vetName}</span> : null}
        </span>
      </span>
    </button>
  );
}
